import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Loader2 } from "lucide-react";
import useRetryFetch from "../../hooks/useRetryFetch";
import { queryClient } from "../../lib/queryClient";
import { trackCapture } from "../../lib/captureAnalytics";
import { mentalModelChoice, planDeeper, routeKey, type DeeperChoice, type DeeperRoute } from "../../lib/diveDeeper";
import { stageMmOpen } from "../../lib/mmHandoff";
import { liftEntity, type MmObjectType } from "../../api/mentalModels";
import { findTakeaway, type UserTakeaway } from "../../api/userTakeaways";
import type { CaptureKind } from "../../api/capture";
import NeedHelpDialog from "../portal/myWorkspace/actionItems/NeedHelpDialog";
import BreakdownDialog from "../portal/myWorkspace/actionItems/BreakdownDialog";
import ActionToActivityDialog from "../portal/myWorkspace/actionItems/ActionToActivityDialog";
import CaptureTraitPanel from "./CaptureTraitPanel";
import { Button } from "../ui/button";

export interface DiveDeeperProps {
  /** The saved item to dive into. `mmType` is the model type, or for other kinds the thought underneath. */
  item: { kind: CaptureKind; id: string; mmType?: MmObjectType };
  reflective: boolean;
  /** Close the sheet (called before navigating away). */
  onDone: () => void;
}

type Step = "ask" | "choose" | "busy" | "error";

/**
 * "Dive deeper?" (spec 2026-11-02 §5): about one saved item (the whole note by default).
 * Remount (key) to retarget it.
 * The specific tool is recommended, alternatives are always shown, and
 * closing the sheet stays one tap.
 */
const DiveDeeper = ({ item, reflective, onDone }: DiveDeeperProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const retryFetch = useRetryFetch();
  const plan = useMemo(
    () => planDeeper({ kind: item.kind, mmType: item.mmType, reflective }),
    [item.kind, item.mmType, reflective]
  );
  const [step, setStep] = useState<Step>("ask");
  const [busyText, setBusyText] = useState("");
  const [choice, setChoice] = useState<DeeperChoice>({ recommended: plan.recommended, alternatives: plan.alternatives });
  const [mmObjectId, setMmObjectId] = useState<string | null>(item.kind === "mental_model" ? item.id : null);
  const [needHelpFor, setNeedHelpFor] = useState<UserTakeaway | null>(null);
  const [breakdownFor, setBreakdownFor] = useState<UserTakeaway | null>(null);
  const [activityFor, setActivityFor] = useState<UserTakeaway | null>(null);
  const [traitOpen, setTraitOpen] = useState(false);

  useEffect(() => {
    trackCapture("capture_deeper_shown", { prominent: plan.prominent });
  }, [plan.prominent]);

  const leaveTo = (path: string) => {
    onDone();
    navigate(path);
  };

  const go = async (route: DeeperRoute) => {
    trackCapture("capture_deeper_opened", { kind: item.kind, route: routeKey(route) });
    try {
      switch (route.route) {
        case "mm_script":
        case "mm_general":
          if (!mmObjectId) {
            setStep("error");
            return;
          }
          stageMmOpen(mmObjectId, route.route === "mm_script" ? route.pathId : undefined, { visit: true });
          leaveTo("/portal");
          return;
        case "need_help": {
          setBusyText(t("capture.deeper.opening"));
          setStep("busy");
          const found = await findTakeaway(retryFetch, item.id);
          if (!found.item) {
            setStep("error");
            return;
          }
          setNeedHelpFor(found.item);
          setStep("ask");
          return;
        }
        case "project_room":
          leaveTo(`/project/${item.id}`);
          return;
        case "trait_panel":
          setTraitOpen(true);
          return;
        case "underneath": {
          setBusyText(t("capture.deeper.lifting"));
          setStep("busy");
          const lifted = await liftEntity(retryFetch, {
            entityType: item.kind === "quality" ? "user_trait" : "user_takeaway",
            entityId: item.id,
            objectType: item.mmType,
          });
          setMmObjectId(lifted.object._id);
          setChoice(mentalModelChoice(lifted.object.objectType));
          setStep("choose");
          return;
        }
        case "keep":
          onDone();
          return;
      }
    } catch {
      setStep("error");
    }
  };

  const openPrompt = () => {
    if (plan.alternatives.length === 0) go(plan.recommended);
    else setStep("choose");
  };

  const label = (r: DeeperRoute) => t(`capture.deeper.route.${routeKey(r)}`);
  const promptText = plan.prompt === "underneath" ? t("capture.deeper.underneath") : t("capture.deeper.prompt");

  return (
    <div className="space-y-3">
      {step === "ask" &&
        (plan.prominent ? (
          <div className="rounded-2xl border border-primary/20 bg-primary/[0.04] p-4 flex items-center justify-between gap-3">
            <p className="text-sm font-medium text-start">{promptText}</p>
            <Button size="sm" className="rounded-full gap-1" onClick={openPrompt}>
              {t("capture.deeper.go")}
              <ArrowRight className="w-3.5 h-3.5 rtl:rotate-180" />
            </Button>
          </div>
        ) : (
          <button type="button" onClick={openPrompt} className="text-sm text-primary hover:underline text-start">
            {promptText}
          </button>
        ))}

      {step === "busy" && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground" role="status">
          <Loader2 className="w-4 h-4 animate-spin" />
          {busyText}
        </p>
      )}

      {step === "choose" && (
        <div className="space-y-2">
          <p className="text-sm font-medium">{t("capture.deeper.chooseHow")}</p>
          <button
            type="button"
            onClick={() => go(choice.recommended)}
            className="w-full rounded-xl border border-primary/30 bg-primary/5 px-4 py-2.5 text-sm text-start flex items-center justify-between gap-2"
          >
            <span>{label(choice.recommended)}</span>
            <span className="text-[11px] uppercase tracking-wider text-primary">{t("capture.deeper.suggested")}</span>
          </button>
          {choice.alternatives.map((r) => (
            <button
              key={routeKey(r)}
              type="button"
              onClick={() => go(r)}
              className="w-full rounded-xl border border-border px-4 py-2.5 text-sm text-start hover:border-primary/30"
            >
              {label(r)}
            </button>
          ))}
        </div>
      )}

      {step === "error" && (
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground">{t("capture.deeper.failed")}</p>
          <Button size="sm" variant="outline" className="rounded-full shrink-0" onClick={() => setStep("ask")}>
            {t("capture.deeper.retry")}
          </Button>
        </div>
      )}

      <NeedHelpDialog
        open={!!needHelpFor}
        onOpenChange={(o) => !o && setNeedHelpFor(null)}
        actionItem={needHelpFor}
        onBreakDown={() => {
          setBreakdownFor(needHelpFor);
          setNeedHelpFor(null);
        }}
        onCreateActivity={() => {
          setActivityFor(needHelpFor);
          setNeedHelpFor(null);
        }}
      />
      <BreakdownDialog
        open={!!breakdownFor}
        onOpenChange={(o) => !o && setBreakdownFor(null)}
        actionItem={breakdownFor}
        onSubtasksCreated={() => {
          setBreakdownFor(null);
          queryClient.invalidateQueries({ queryKey: ["home-today-actions"] });
          onDone();
        }}
      />
      <ActionToActivityDialog
        open={!!activityFor}
        onOpenChange={(o) => !o && setActivityFor(null)}
        actionItem={activityFor}
        onActivityCreated={() => {
          setActivityFor(null);
          onDone();
        }}
      />
      {item.kind === "quality" && (
        <CaptureTraitPanel
          traitId={item.id}
          open={traitOpen}
          onClose={() => setTraitOpen(false)}
          onFailed={() => {
            setTraitOpen(false);
            setStep("error");
          }}
        />
      )}
    </div>
  );
};

export default DiveDeeper;
